// Third-party
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { SortingRule } from 'react-table';
import { get } from 'lodash';

// Enums
import { PERSISTENCE_KEYS } from '@/utils/persistence';
import { DATA_SOURCE_TAB_CATEGORY } from '@/enums/DataSource';

// Types
import { DataSourceTabs as DataSourceTabsType } from '@/types/app-config';
import { PORTFOLIO_METRICS_FIELDS, PORTFOLIO_TABLE_COLUMNS } from '../../../../enums/Portfolio';

// Utils
import { FormattedMessage, formatMessage } from '../../../../utils/i18n';
import { resetLocalStoragePersistenceSource } from '@/utils/persistence';
import { getPortfolioMissingHoldings } from '../../../../utils/portfolio/utils';
import { getFilteredDataSourceTabs } from '@/utils/data-source';
import { userHasPermissionEnabled, PERMISSIONS, userHasPermission } from '../../../../utils/permissions';
import localStorageUtils from '@/utils/local-storage';

// Components
import { SecondaryLink } from '../../buttons';
import { LoadingSpinner } from '../../common';
import DisclaimerFooter from '../../disclaimers/DisclaimerFooter';
import { SearchInput } from '../../inputs';
import { GenericEmptyState } from '../../misc';
import { PortfoliosTable } from '../../tables';
import User from '@/types/user';
import Portfolio from '@/types/portfolio';
import DataSourceTabs from '@/views/components/data-source/DataSourceTabs';

type Props = {
  user: User;
  backButtonText: string;
  emptyStateHeader: string;
  isLoading: boolean;
  searchQuery: string;
  selectedContact: any;
  isThemeLight?: boolean;
  persistenceSource?: string;
  isPostMessageActive: boolean;
  onBack: () => void;
  onLoadMorePortfolios: () => void;
  onSearchPortfolios: (query: string) => void;
  onSelectPortfolios: (portfolios: Portfolio[]) => void;
  onSortPortfolios: (sortingRules: SortingRule[]) => void;
  onUnselectPortfolios: (portfolios: Portfolio[]) => void;
  setPersistenceSource: (user: User, key: string, value?: string) => void;
  setIsActivePortfolioPostMessage: (isActive: boolean) => void;
};

const getDisplayedColumns = (user: User) =>
  [
    {
      key: PORTFOLIO_TABLE_COLUMNS.NAME,
    },
    {
      key: PORTFOLIO_TABLE_COLUMNS.RISK_PROFILE,
    },
    {
      key: PORTFOLIO_TABLE_COLUMNS.LOCK,
    },
    {
      key: PORTFOLIO_TABLE_COLUMNS.SHOW_DETAILS,
      hidden: !userHasPermissionEnabled(user, PERMISSIONS.SHOW_PORTFOLIO_DETAILS),
    },
    {
      key: PORTFOLIO_TABLE_COLUMNS.COMPARE,
      hidden: false,
    },
  ]
    .filter(col => !col.hidden)
    .map(col => col.key);

const LegacyContactPortfoliosList = ({
  user,
  backButtonText,
  emptyStateHeader,
  isLoading,
  searchQuery,
  selectedContact,
  isPostMessageActive,
  onBack,
  onLoadMorePortfolios,
  onSearchPortfolios,
  onSelectPortfolios,
  onSortPortfolios,
  onUnselectPortfolios,
  setIsActivePortfolioPostMessage,
  setPersistenceSource,
  isThemeLight,
}: Props) => {
  const dataSourceTabs: DataSourceTabsType[] | null = getFilteredDataSourceTabs(user, DATA_SOURCE_TAB_CATEGORY.PORTFOLIOS, isPostMessageActive);
  const displayedColumns = getDisplayedColumns(user);
  const displayedMetrics = [
    PORTFOLIO_METRICS_FIELDS.MER,
    PORTFOLIO_METRICS_FIELDS.YEAR_ONE,
    PORTFOLIO_METRICS_FIELDS.YEAR_THREE,
    PORTFOLIO_METRICS_FIELDS.SIZE,
    PORTFOLIO_METRICS_FIELDS.HOLDINGS,
  ];
  const isTableHidden = isLoading || (selectedContact && (!selectedContact.portfolios || selectedContact.portfolios.length === 0));

  const [activeDataSource, setActiveDataSource] = useState<string>(dataSourceTabs?.[0]?.value ?? '');

  useEffect(() => {
    // Reset the Persistence Source when the component mounts
    const isPostMessageDataSourceTabExist = dataSourceTabs?.find(tab => tab.isPostMessage);
    const isInitialDataSourceTabPostMessage = isPostMessageActive && isPostMessageDataSourceTabExist;

    if (isInitialDataSourceTabPostMessage) {
      setIsActivePortfolioPostMessage(false);
    }
    resetLocalStoragePersistenceSource(localStorageUtils.STORAGE_KEYS.CONTACT_PORTFOLIO_PERSISTENCE_SOURCE, user, () => onSearchPortfolios(searchQuery));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPostMessageActive, searchQuery, user, onSearchPortfolios, setIsActivePortfolioPostMessage]);

  const renderEmptyMessage = () => <GenericEmptyState header={emptyStateHeader} />;

  const renderNoResultsMessage = () => (
    <GenericEmptyState
      header={formatMessage('EmptyState.NoResults.Title')}
      body={formatMessage('EmptyState.NoResults.Text')}
      actionLabel={formatMessage('EmptyState.NoResults.ClearSearch')}
      onAction={() => onSearchPortfolios('')}
    />
  );

  const renderNoResults = () => {
    if (searchQuery) {
      return renderNoResultsMessage();
    }

    return renderEmptyMessage();
  };

  const onSelectDataSource = (dataSourceValue: string) => {
    if (!dataSourceTabs) return;

    setActiveDataSource(dataSourceValue);

    const activeDataSource = dataSourceTabs.find(tab => tab.value === dataSourceValue);
    let persistenceSource;

    if (activeDataSource?.isPostMessage) {
      setIsActivePortfolioPostMessage(true);
    } else {
      setIsActivePortfolioPostMessage(false);
      persistenceSource = activeDataSource?.persistenceKey;
    }

    setPersistenceSource(user, localStorageUtils.STORAGE_KEYS.CONTACT_PORTFOLIO_PERSISTENCE_SOURCE, persistenceSource);
    onSearchPortfolios(searchQuery);
  };

  const contactName = userHasPermission(user, PERMISSIONS.CONTACTS_LAST_NAME)
    ? `${selectedContact.contact.fname} ${selectedContact.contact.lname}`
    : selectedContact.contact.fname;

  // Data Source Tabs related logic
  const isContactPersistenceSourceActive = localStorageUtils.getPersistenceSource(user, localStorageUtils.STORAGE_KEYS.CONTACT_PERSISTENCE_SOURCE) != null;
  const contactDataSourceTabs: DataSourceTabsType[] = get(user, 'appConfig.dataSourceTabs.contacts', []);
  const hasExternalContacts =
    !contactDataSourceTabs.filter(tab => tab.persistenceKey === PERSISTENCE_KEYS.EXISTING_CLIENT_CONTACT).length || isContactPersistenceSourceActive;

  return (
    <div className="contact-portfolio-list">
      <div data-private data-dd-privacy="mask" className="contact-portfolios-title d-flex">
        <SecondaryLink onClick={onBack} icon="arrow-left">
          <FormattedMessage id={backButtonText} />
        </SecondaryLink>

        <h1>
          <FormattedMessage id="ContactPortfoliosModal.ContactTitle" values={{ name: contactName }} data-private data-dd-privacy="mask" />
        </h1>
      </div>

      <div className={classNames('contact-portfolios-table d-flex flex-column')}>
        <SearchInput
          compact
          placeholder={formatMessage('ContactPortfoliosModal.SearchPortfolios')}
          onChange={onSearchPortfolios}
          value={searchQuery}
          data-private
          data-dd-privacy="mask"
        />

        {dataSourceTabs && hasExternalContacts && (
          <div className="ms-4 mt-2">
            <DataSourceTabs disabled={isLoading} tabs={dataSourceTabs} value={activeDataSource} onChange={onSelectDataSource} />
          </div>
        )}

        {isLoading && <LoadingSpinner className="initial-loading" message={formatMessage('ContactPortfoliosModal.LoadingPortfolios')} />}

        {!isLoading && selectedContact.portfolios && selectedContact.portfolios.length === 0 && renderNoResults()}

        <PerfectScrollbar className="portfolios-scrollbar">
          <PortfoliosTable<Portfolio>
            className={classNames({ 'visually-hidden': isTableHidden })}
            displayedColumns={displayedColumns}
            displayedMetrics={displayedMetrics}
            showLoadMore={selectedContact.hasMore.portfolios}
            portfolios={selectedContact.portfolios}
            selectedTitle="PortfoliosTable.SelectedTitle.Portfolios"
            onLoadMore={onLoadMorePortfolios}
            onPortfoliosSelected={onSelectPortfolios}
            onUnselectPortfolios={onUnselectPortfolios}
            onSort={onSortPortfolios}
            forceSelectable
            canEditPredicate={portfolio =>
              userHasPermission(user, PERMISSIONS.PORTFOLIOS_EDIT) && (!portfolio.availableActions || portfolio.availableActions.edit)
            }
            canClonePredicate={portfolio =>
              userHasPermission(user, PERMISSIONS.PORTFOLIOS_CLONE) && (!portfolio.availableActions || portfolio.availableActions.clone)
            }
            missingProductPredicate={portfolio => getPortfolioMissingHoldings(portfolio).length > 0}
            isThemeLight={isThemeLight}
            user={user}
          />
        </PerfectScrollbar>

        {!isLoading && selectedContact.portfolios && selectedContact.portfolios.length > 0 && (
          <DisclaimerFooter disclaimers={[formatMessage('Disclaimers.NET_PERFORMANCE')]} />
        )}
      </div>
    </div>
  );
};

export default LegacyContactPortfoliosList;
